import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import logo from '../assets/logo.svg';
import bgImage from '../assets/bg.webp';

interface LoginResponse {
  sessionId: string;
  user: {
    id: string;
    email: string;
    name: string;
  };
  hasMultipleWorkspaces: boolean;
  onboardingRequired?: boolean;
  workspace?: {
    id: string;
    name: string;
  };
}

/**
 * AuthLogin page for OIDC authorization code flow.
 * Handles email/password login and creates a central SSO session.
 * Preserves OAuth params in `continue` query param for flow resumption.
 */
export function AuthLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get the continue URL (encoded OAuth params)
  const continueUrl = searchParams.get('continue') || '';
  const registered = searchParams.get('registered') === 'true';
  const authError = searchParams.get('error') || '';

  // Show success toast for newly registered users
  useEffect(() => {
    if (registered) {
      toast.success('Account created successfully! Please sign in.');
    }
  }, [registered]);

  // Surface a friendly message when the Google sign-in session expired
  // (the OAuth callback redirects here with ?error=session_expired when the
  // pending OAuth state is invalid/expired). The user can simply click
  // "Sign in with Google" again.
  useEffect(() => {
    if (authError === 'session_expired') {
      setError('Your Google sign-in session expired. Please try again.');
    }
  }, [authError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Client-side validation
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    if (!password) {
      toast.error('Please enter your password');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/oidc/session/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Important for cookies
        body: JSON.stringify({ email: email.trim(), password }),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMsg = data.message || 'Invalid email or password';
        setError(errorMsg);
        toast.error(errorMsg);
        return;
      }

      const loginData = data as LoginResponse;

      // If user has multiple workspaces, redirect to workspace picker
      if (loginData.hasMultipleWorkspaces) {
        toast.success('Welcome back! Choose your workspace.');
        const workspaceUrl = continueUrl
          ? `/auth/workspace?continue=${encodeURIComponent(continueUrl)}`
          : '/auth/workspace';
        navigate(workspaceUrl);
        return;
      }

      // If workspace needs onboarding, redirect to onboarding flow
      if (loginData.onboardingRequired) {
        toast.success('Welcome! Let\'s set up your workspace.');
        // If the user originally entered through /start/{mode}, route them
        // back there so StartEntry can persist onboardingMode='personal' or
        // 'business' before /onboarding/type renders. Jumping straight to
        // /onboarding/type skips that mode-setting step and leaves the
        // workspace with onboardingMode=null — which makes /onboarding/type
        // (and OnboardingLayout's progress bar) render the business default
        // even when the user came in via /start/personal.
        if (continueUrl && continueUrl.startsWith('/start/')) {
          navigate(continueUrl);
          return;
        }
        // No continue context → the modern Personal/Business chooser
        // (/onboarding/new), not the deprecated /onboarding/type picker.
        // With an OIDC-flow continue, preserve existing routing so the
        // suite-app flow is unchanged.
        const onboardingUrl = continueUrl
          ? `/onboarding/type?continue=${encodeURIComponent(continueUrl)}`
          : '/onboarding/new';
        navigate(onboardingUrl);
        return;
      }

      // Single workspace with completed onboarding - continue OAuth flow
      toast.success('Welcome back!');
      if (continueUrl) {
        // continueUrl already contains full path (e.g. /api/oidc/authorize?response_type=code&...)
        window.location.href = continueUrl;
      } else {
        // No continue URL - land on the workspace home, not the admin
        // control plane at `/` (which is admin-gated). Admins can still
        // navigate to `/` directly; `/` is unchanged.
        navigate('/home');
      }
    } catch (err) {
      const errorMsg = 'Network error. Please try again.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[rgb(24,24,24)] bg-cover bg-center flex flex-col items-center p-4"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      <img src={logo} alt="LevelUp" className="h-10 mt-8" />
      <div className="flex-1 flex items-center justify-center w-full">
        <div className="bg-white shadow-lg rounded-lg p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">
            Sign in
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Enter your credentials to continue
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              autoFocus
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Password
              </label>
              <Link
                to={
                  continueUrl
                    ? `/auth/forgot-password?continue=${encodeURIComponent(continueUrl)}`
                    : '/auth/forgot-password'
                }
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-[#25BD71] text-white py-2 px-4 rounded-md font-medium hover:bg-[#1EA35E] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">Or continue with</span>
          </div>
        </div>

        {/* Google Sign-in Button */}
        <button
          type="button"
          onClick={() => {
            // Suite-app OIDC flow: continueUrl is the /api/oidc/authorize URL.
            // Append provider=google so the existing OIDC-driven Google flow runs.
            if (continueUrl.startsWith('/api/oidc/authorize')) {
              const separator = continueUrl.includes('?') ? '&' : '?';
              window.location.href = `${continueUrl}${separator}provider=google`;
              return;
            }
            // Otherwise (no continueUrl, or a frontend route like /start/personal):
            // use the backend-initiated session flow. With no continueUrl,
            // default returnTo to the Central /home route — never bare `/`,
            // which is the admin control plane. The backend still validates it.
            const returnTo = continueUrl || '/home';
            window.location.href = `/api/auth/google/start?returnTo=${encodeURIComponent(returnTo)}`;
          }}
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-3 bg-white border border-gray-300 text-gray-700 py-2 px-4 rounded-md font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Sign in with Google
        </button>

        {/* Sign up link */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Don't have an account?{' '}
            <Link
              to={
                continueUrl
                  ? `/auth/signup?continue=${encodeURIComponent(continueUrl)}`
                  : '/auth/signup'
              }
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              Sign up
            </Link>
          </p>
        </div>

        {import.meta.env.DEV && (
          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-md">
            <p className="text-xs text-amber-800 font-medium mb-2">
              Dev Credentials:
            </p>
            <ul className="text-xs text-amber-700 space-y-1">
              <li>
                <code className="bg-amber-100 px-1 rounded">owner@levelup.dev</code>
              </li>
              <li>
                <code className="bg-amber-100 px-1 rounded">multi@levelup.dev</code> (multi-workspace)
              </li>
              <li>
                Password: <code className="bg-amber-100 px-1 rounded">devpassword123</code>
              </li>
            </ul>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
